import { gsap } from 'gsap'
import { generateIconElement, ICON } from "../../Icons/Icons"
import { SOCKET_EVENTS } from "../../../socket/Events";

const SOCKET_CONNECTING_ICON = generateIconElement(ICON.SERVER, { styles: { color: '#E39741' } }).prop('outerHTML')
const SOCKET_CONNECTED_ICON = generateIconElement(ICON.SERVER, { styles: { color: '#7CAF00' } }).prop('outerHTML')

export class SystemNotification {
  constructor({ model, socket }) {
    this.model = model
    this.socket = socket

    this.$container = $(this.#containerTemplate())
    this.$container.css('opacity', 0) // Set initial opacity to 0

    this.showConnectedNotification = false
    this.currentType = null
    this.hideTimeout = null

    this.setupEventListeners()
  }

  #containerTemplate() {
    return `<div class="system-notification-container"></div>`
  }
  // ${showReconnect ? `<a href="javascript:void(0)" class="system-notification__reconnect" data-lang="reconnect">${$trans('reconnect')}</a>` : ''}
  #messageTemplate({
    type = '',
    icon = '',
    message = '',
    showReconnect = false,
    showLoadingBar = false,
    classes = '', // Additional classes
    closeable = false,
  }) {
    const additionClasses = [
      type ? `toast-notification--${type}` : '',
      closeable ? 'system-notification--closeable' : '',
      classes,
    ].filter((i) => i).join(' ')
    return `
      <div class="system-notification toast-notification ${additionClasses}">
        ${icon ? `<div class="toast-notification__icon">${icon}</div>` : ''}
        <span class="toast-notification__text">${message}</span>
        ${closeable ? `<span class='material-icons md-18 clickable confirm-close'>close</span>` : ''}
        ${showLoadingBar ? `<div class="toast-notification__loading-bar"></div>` : ''}
      </div>
    `
  }

  attachTo(element) {
    if (element) {
      $(element).append(this.$container)
    }
  }

  activeFloatingMode() {
    this.$container.addClass('toast-notification--floating')
  }

  setupEventListeners() {
    this.checkConnection()

    // Monitor internet connection
    $(window).on('online', () => this.handleOnlineStatus(true))
    $(window).on('offline', () => this.handleOnlineStatus(false))

    // Monitor connection quality
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => this.checkConnectionQuality())
    }

    // Monitor socket connection
    let isInitialConnectionChecked = false
    this.model.addEventListener(SOCKET_EVENTS.SOCKET_CONNECT_ERROR, () => {
      if (isInitialConnectionChecked) {
        return
      }
      isInitialConnectionChecked = true

      this.#showSocketConnectionLostNotification()
    })

    this.socket?.on('connect', () => {
      isInitialConnectionChecked = true

      this.handleSocketConnection(true)
    })
    this.socket?.on('disconnect', () => this.handleSocketConnection(false))

    $(document).on('click', '.system-notification--closeable', () => this.playAnimation(false))
  }

  playAnimation(show = true, duration = 0.4) {
    return gsap.to(this.$container[0], {
      opacity: show ? 1 : 0,
      duration,
      onComplete: () => {
        if (!show) {
          this.$container.empty()
          this.currentType = null
        }
      }
    })
  }

  clearHideTimeout() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  clear() {
    this.clearHideTimeout()

    if ($('.system-notification').length) {
      this.playAnimation(false)
    }
  }

  createNotification(
    type = '',
    message = '',
    {
      icon = '',
      showReconnect = false,
      showLoadingBar = false,
      classes = '',
      closeable = false,
    } = {}
  ) {
    this.clearHideTimeout()

    if (!message) {
      this.clear()
      return
    }

    const settings = {
      type,
      message,
      icon,
      classes,
      showReconnect,
      showLoadingBar,
      closeable,
    }

    const $message = $(this.#messageTemplate(settings))

    if (showReconnect) {
      $message.find('.system-notification__reconnect').on('click', () => this.handleReconnect())
    }

    // Fade out current content if any
    if (this.$container.children().length > 0) {
      gsap.to(this.$container[0], {
        opacity: 0,
        duration: 0.2,
        onComplete: () => {
          this.$container.empty().append($message)
          this.playAnimation(true)
        }
      })
    } else {
      // If container is empty, just add new content and fade in
      this.$container.append($message)
      this.playAnimation(true)
    }

    this.currentType = type
  }

  setHideTimeout(delay = 3000) {
    this.clearHideTimeout()
    const currentType = this.currentType

    this.hideTimeout = setTimeout(() => {
      // Only hide if the type hasn't changed
      if (this.currentType === currentType) {
        this.clear()
      }
    }, delay)
  }

  async handleReconnect() {
    this.showConnectedNotification = true
    this.#showSocketConnectingNotification()

    const startTime = Date.now()
    const timeout = 5000 // 5 seconds timeout

    while (Date.now() - startTime < timeout) {
      try {
        await this.socket?.socket.connect()
        if (this.socket?.socket.connected) {
          this.#showSocketReconnectedNotification()
          this.setHideTimeout(3000)
          return
        }
      } catch (error) {
        console.error('handleReconnect', error)
      } finally {
        // If there's still time, wait 500ms before trying again
        if (Date.now() - startTime < timeout) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    }

    // If we get here, we failed to reconnect within 5 seconds
    console.log('Failed to reconnect to Bagless server', this.socket)
    if (!this.socket?.socket.connected || this.socket?.socket.disconnected) {
      this.#showSocketConnectionLostNotification()
    }
  }

  handleOnlineStatus(isOnline) {
    if (!isOnline) {
      this.#showNetworkConnectionLostNotification()
    } else {
      this.clear()
      this.checkConnectionQuality()
    }
  }

  checkConnectionQuality() {
    if (!navigator.onLine) return

    if ('connection' in navigator) {
      const connection = navigator.connection
      const isLowQuality = connection.downlink < 1 || connection.rtt > 500

      if (isLowQuality) {
        this.#showPoorNetworkConnectionNotification()
      } else {
        this.clear()
      }
    }
  }

  handleSocketConnection(isConnected) {
    if (!isConnected) {
      this.#showSocketConnectionLostNotification()
    } else {
      if (!this.showConnectedNotification) {
        this.showConnectedNotification = true
        this.clear()
        return
      }

      this.#showSocketReconnectedNotification()
      this.setHideTimeout(3000)
    }
  }

  checkConnection() {
    if (!navigator.onLine) {
      this.#showNetworkConnectionLostNotification()
      return
    }

    this.checkConnectionQuality()
  }

  #showSocketConnectionLostNotification() {
    if (this.model.isFrozenSession) {
      return
    }
    this.createNotification('error', $trans('socket-connection-lost'), {
      showReconnect: true,
      icon: ICON.SERVER_OFF,
    })
  }

  #showSocketReconnectedNotification() {
    if (this.model.isFrozenSession) {
      return
    }
    this.createNotification('success', $trans('socket-reconnected'), {
      icon: SOCKET_CONNECTED_ICON,
    })
  }

  #showSocketConnectingNotification() {
    if (this.model.isFrozenSession) {
      return
    }
    this.createNotification('info', $trans('socket-reconnecting'), {
      showLoadingBar: true,
      icon: SOCKET_CONNECTING_ICON,
    })
  }

  #showNetworkConnectionLostNotification() {
    if (this.model.isFrozenSession) {
      return
    }
    this.createNotification('error', $trans('network-connection-lost'), {
      icon: ICON.WIFI_LOST,
    })
  }

  #showPoorNetworkConnectionNotification() {
    if (this.model.isFrozenSession) {
      return
    }
    this.createNotification('warning', $trans('network-poor-connection'), {
      icon: ICON.WIFI_POOR,
      closeable: true,
    })
  }
}
